/**
 * Browsers mandate image/png support for clipboard images, but rich editors such as
 * Teams may instead consume text/html. For GIFs we provide both: a PNG fallback and
 * HTML containing the original GIF as a data URL. The destination chooses the format.
 */
export async function toClipboardPngBlob(source: Blob): Promise<Blob> {
  if (source.type === 'image/png') return source;

  const bitmap = await createImageBitmap(source);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare image for clipboard.');
    ctx.drawImage(bitmap, 0, 0);

    const png = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode PNG for clipboard.'))),
        'image/png'
      );
    });
    return png;
  } finally {
    bitmap.close();
  }
}

export async function writeImageToClipboard(source: Blob): Promise<void> {
  if (!navigator.clipboard || typeof ClipboardItem === 'undefined') {
    throw new Error('Clipboard image copy is not supported in this browser.');
  }

  const pngBlob = await toClipboardPngBlob(source);
  const representations: Record<string, Promise<Blob>> = {
    'image/png': Promise.resolve(pngBlob)
  };

  if (source.type === 'image/gif') {
    const dataUrl = await blobToDataUrl(source);
    representations['text/html'] = Promise.resolve(
      new Blob([`<img src="${dataUrl}" alt="Animated GIF">`], {
        type: 'text/html'
      })
    );
  }

  // Pass promises so Chromium can sanitize each representation before committing.
  await navigator.clipboard.write([
    new ClipboardItem(representations)
  ]);
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return `data:${blob.type || 'application/octet-stream'};base64,${btoa(binary)}`;
}
